import React from 'react';
import {NotificationService} from '../../common/notification';
import {AccessBlacklistRow, AccessBlacklistType} from '../../models/accessBlacklist';
import {
    accessBlacklistAddApi,
    accessBlacklistGetUserApi,
    accessBlacklistRemoveUserApi
} from './accessBlacklistApi';

export type UserAccessBlacklistState = {
    row: AccessBlacklistRow | null;
    loading: boolean;
    isBlocked: boolean;
    reload: () => Promise<void>;
    block: (reason: string, expiresAt?: string | null) => Promise<boolean>;
    unblock: () => Promise<boolean>;
};

export function useUserAccessBlacklist(userId: bigint, refreshKey = 0): UserAccessBlacklistState {
    const [row, setRow] = React.useState<AccessBlacklistRow | null>(null);
    const [loading, setLoading] = React.useState(false);

    const reload = React.useCallback(async () => {
        if (!userId || userId <= BigInt(0)) {
            setRow(null);
            return;
        }
        setLoading(true);
        try {
            const res = await accessBlacklistGetUserApi(userId);
            if (res.result) {
                setRow(res.value ?? null);
            } else {
                NotificationService.alertError('Не удалось получить статус черного списка');
            }
        } finally {
            setLoading(false);
        }
    }, [userId]);

    React.useEffect(() => {
        reload().catch(console.error);
    }, [reload, refreshKey]);

    const block = React.useCallback(async (reason: string, expiresAt?: string | null) => {
        const res = await accessBlacklistAddApi(
            AccessBlacklistType.User,
            userId.toString(),
            reason.trim(),
            expiresAt || null
        );
        if (res.result) {
            NotificationService.alertSuccess('Пользователь заблокирован: доступ к API закрыт (403)');
            await reload();
            return true;
        }
        NotificationService.alertError('Не удалось добавить в черный список');
        return false;
    }, [userId, reload]);

    const unblock = React.useCallback(async () => {
        const res = await accessBlacklistRemoveUserApi(userId);
        if (res.result) {
            NotificationService.alertSuccess('Пользователь убран из черного списка');
            await reload();
            return true;
        }
        NotificationService.alertError('Не удалось убрать из черного списка');
        return false;
    }, [userId, reload]);

    return {
        row,
        loading,
        isBlocked: row != null,
        reload,
        block,
        unblock,
    };
}
